import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, X, Check, Download, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];

const fmtMoney = (n) => (+n).toFixed(2).replace(/\.00$/, "");

const ordinal = (n) => (n === 2 ? "2nd" : n === 3 ? "3rd" : `${n}th`);

const CHAUFFEUR_STEPS = [
  { key: "en_route", label: "En route" },
  { key: "arrived", label: "Arrived" },
  { key: "aboard", label: "Aboard" },
];
const STATUS_CLS = {
  pending: "border-yellow-600/50 text-yellow-500",
  confirmed: "border-emerald-600/50 text-emerald-400",
  completed: "border-zinc-600 text-zinc-400",
  cancelled: "border-red-700/50 text-red-400",
};

const emptyVehicle = {
  name: "", category: "VIP Sedan", image: "", gallery: "", passengers: 3, luggage: 2,
  hourly_rate: 95, description: "", specs: "", available: true,
};

const inputCls =
  "w-full bg-[#0F1012] border border-zinc-800 focus:border-amber-500/60 outline-none px-4 py-3 text-sm text-zinc-200 placeholder:text-zinc-600 transition-colors";

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [moreBookings, setMoreBookings] = useState(false);
  const [moreEnquiries, setMoreEnquiries] = useState(false);
  const [revenue, setRevenue] = useState(null);
  const [exportMonth, setExportMonth] = useState("");
  const [blackouts, setBlackouts] = useState([]);
  const [blackoutDate, setBlackoutDate] = useState("");
  const [blackoutVehicle, setBlackoutVehicle] = useState("");
  const [blackoutReason, setBlackoutReason] = useState("");
  const [editingNote, setEditingNote] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tracking, setTracking] = useState({});
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyVehicle);
  const [reviews, setReviews] = useState([]);
  const [promos, setPromos] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [promoForm, setPromoForm] = useState({ code: "", percent: "", amount_off: "", expires: "", max_uses: "" });
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => navigate("/admin/login"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/bookings", { params: { skip: 0, limit: 50 } }).then((r) => {
      setBookings(r.data);
      setMoreBookings(r.data.length === 50);
    }).catch(() => {});
    api.get("/vehicles/all").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/enquiries", { params: { skip: 0, limit: 50 } }).then((r) => {
      setEnquiries(r.data);
      setMoreEnquiries(r.data.length === 50);
    }).catch(() => {});
    api.get("/payments/summary").then((r) => setRevenue(r.data)).catch(() => {});
    api.get("/blackouts").then((r) => setBlackouts(r.data)).catch(() => {});
    api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/admin/promos").then((r) => setPromos(r.data)).catch(() => {});
    api.get("/admin/vouchers").then((r) => setVouchers(r.data)).catch(() => {});
  }, [user]);

  const loadMoreBookings = async () => {
    try {
      const { data } = await api.get("/bookings", { params: { skip: bookings.length, limit: 50 } });
      setBookings((b) => [...b, ...data]);
      setMoreBookings(data.length === 50);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const loadMoreEnquiries = async () => {
    try {
      const { data } = await api.get("/enquiries", { params: { skip: enquiries.length, limit: 50 } });
      setEnquiries((q) => [...q, ...data]);
      setMoreEnquiries(data.length === 50);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/bookings/export", {
        params: exportMonth ? { month: exportMonth } : {},
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = exportMonth
        ? `aureus-reservations-${exportMonth}.csv`
        : `aureus-reservations-${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(exportMonth ? `Reservations for ${exportMonth} exported.` : "Reservations exported.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const refundPayment = async (b, kind) => {
    try {
      const { data } = await api.post("/payments/refund", { booking_id: b.id, kind });
      setBookings((bs) => bs.map((x) => (x.id === b.id ? { ...x, [`${kind}_paid`]: false } : x)));
      toast.success(`${kind === "deposit" ? "Deposit" : "Balance"} of $${data.amount} refunded.`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportEnquiriesCsv = async () => {
    try {
      const { data } = await api.get("/enquiries/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `aureus-enquiries-${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Enquiries exported.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const collectBalance = async (b) => {
    try {
      const { data } = await api.post("/payments/balance", { booking_id: b.id, origin_url: window.location.origin });
      setBookings((bs) => bs.map((x) => (x.id === b.id ? { ...x, balance_checkout_url: data.checkout_url } : x)));
      toast.success(`Balance link for $${data.amount} emailed to ${b.email}.`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyBalanceLink = (b) => {
    navigator.clipboard.writeText(b.balance_checkout_url);
    toast.success("Payment link copied — send it to the client anytime.");
  };

  const setChauffeur = async (id, chauffeur_status) => {
    try {
      await api.patch(`/bookings/${id}/chauffeur`, { chauffeur_status });
      setBookings((bs) => bs.map((x) => (x.id === id ? { ...x, chauffeur_status } : x)));
      if (chauffeur_status === "en_route") toast.success("Client notified — chauffeur en route.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveNote = async (b) => {
    try {
      const { data } = await api.put("/clients/notes", { email: b.email, note: noteDraft });
      setBookings((bs) => bs.map((x) => (x.email.toLowerCase() === b.email.toLowerCase() ? { ...x, client_note: data.note } : x)));
      setEditingNote(null);
      toast.success("Client note saved.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addBlackout = async () => {
    if (!blackoutDate) return;
    try {
      const { data } = await api.post("/blackouts", { date: blackoutDate, vehicle_id: blackoutVehicle, reason: blackoutReason });
      setBlackouts((bl) => [...bl, data].sort((a, b) => a.date.localeCompare(b.date)));
      setBlackoutDate("");
      setBlackoutReason("");
      toast.success("Date blocked — new bookings are stopped instantly.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeBlackout = async (id) => {
    try {
      await api.delete(`/blackouts/${id}`);
      setBlackouts((bl) => bl.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleReview = async (r, approved) => {
    try {
      await api.patch(`/admin/reviews/${r.id}`, { approved });
      setReviews((rs) => rs.map((x) => (x.id === r.id ? { ...x, approved } : x)));
      toast.success(approved ? "Review published on the site." : "Review unpublished.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteReview = async (id) => {
    try {
      await api.delete(`/admin/reviews/${id}`);
      setReviews((rs) => rs.filter((x) => x.id !== id));
      toast.success("Review removed.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const createPromo = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/promos", {
        code: promoForm.code,
        percent: Number(promoForm.percent) || 0,
        amount_off: Number(promoForm.amount_off) || 0,
        expires: promoForm.expires,
        max_uses: Number(promoForm.max_uses) || 0,
      });
      setPromos((p) => [data, ...p]);
      setPromoForm({ code: "", percent: "", amount_off: "", expires: "", max_uses: "" });
      toast.success(`Code ${data.code} is live.`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const togglePromo = async (p, active) => {
    try {
      await api.patch(`/admin/promos/${p.id}`, { active });
      setPromos((ps) => ps.map((x) => (x.id === p.id ? { ...x, active } : x)));
      toast.success(active ? `${p.code} is live.` : `${p.code} paused.`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deletePromo = async (id) => {
    try {
      await api.delete(`/admin/promos/${id}`);
      setPromos((ps) => ps.filter((x) => x.id !== id));
      toast.success("Code deleted.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const [searchQ, setSearchQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [uploading, setUploading] = useState("");
  const imageFileRef = useRef(null);
  const galleryFileRef = useRef(null);

  const refreshBookings = async () => {
    try {
      const { data } = await api.get("/bookings", { params: { skip: 0, limit: 50 } });
      setBookings(data);
      setMoreBookings(data.length === 50);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const searchBookings = async () => {
    if (!searchQ.trim()) {
      refreshBookings();
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get("/bookings", { params: { q: searchQ.trim(), limit: 200 } });
      setBookings(data);
      setMoreBookings(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSearching(false);
    }
  };

  const deleteBooking = async (b) => {
    if (!window.confirm(`Delete reservation ${b.reference} permanently? This cannot be undone.`)) return;
    try {
      await api.delete(`/bookings/${b.id}`);
      setBookings((bs) => bs.filter((x) => x.id !== b.id));
      toast.success(`${b.reference} deleted.`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const purgeCancelled = async () => {
    if (!window.confirm("Delete ALL cancelled reservations permanently? This cannot be undone.")) return;
    try {
      const { data } = await api.post("/admin/bookings/purge-cancelled");
      toast.success(data.deleted ? `${data.deleted} cancelled reservation${data.deleted > 1 ? "s" : ""} removed.` : "No cancelled reservations to remove.");
      refreshBookings();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadImage = async (file, target) => {
    if (!file) return;
    setUploading(target);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      if (target === "image") setForm((f) => ({ ...f, image: data.url }));
      else setForm((f) => ({ ...f, gallery: f.gallery ? `${f.gallery}, ${data.url}` : data.url }));
      toast.success("Photo uploaded — save the vehicle to keep it.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading("");
    }
  };

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/bookings/${id}`, { status });
      setBookings((b) => b.map((x) => (x.id === id ? data : x)));
      toast.success(`Booking ${data.reference} marked ${status}.`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openEditor = (v) => {
    if (v) {
      setEditing(v.id);
      setForm({ ...v, specs: v.specs.join(", "), gallery: (v.gallery || []).join(", ") });
    } else {
      setEditing("new");
      setForm(emptyVehicle);
    }
  };

  const saveVehicle = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      passengers: Number(form.passengers),
      luggage: Number(form.luggage),
      hourly_rate: Number(form.hourly_rate),
      specs: form.specs.split(",").map((s) => s.trim()).filter(Boolean),
      gallery: form.gallery.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing === "new") {
        const { data } = await api.post("/vehicles", payload);
        setVehicles((v) => [...v, data]);
        toast.success(`${data.name} added to the fleet.`);
      } else {
        const { data } = await api.put(`/vehicles/${editing}`, payload);
        setVehicles((v) => v.map((x) => (x.id === editing ? data : x)));
        toast.success(`${data.name} updated.`);
      }
      setEditing(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const trackFlight = async (b) => {
    setTracking((t) => ({ ...t, [b.id]: { loading: true } }));
    try {
      const { data } = await api.get(`/flights/${b.flight_number}`);
      setTracking((t) => ({ ...t, [b.id]: { data } }));
    } catch (err) {
      setTracking((t) => ({ ...t, [b.id]: { error: formatApiError(err) } }));
    }
  };

  const removeVehicle = async (id) => {
    try {
      await api.delete(`/vehicles/${id}`);
      setVehicles((v) => v.filter((x) => x.id !== id));
      toast.success("Vehicle removed from the fleet.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  if (!user) {
    return (
      <main className="min-h-screen bg-[#0F1012] flex items-center justify-center">
        <p className="font-mono-lux text-xs uppercase tracking-[0.3em] text-zinc-600" data-testid="admin-loading">Entering…</p>
      </main>
    );
  }

  const filteredBookings = statusFilter === "all" ? bookings : bookings.filter((b) => b.status === statusFilter);

  return (
    <main className="min-h-screen bg-[#0F1012]" data-testid="admin-dashboard">
      <header className="border-b border-amber-500/10 sticky top-0 bg-[#0F1012]/90 backdrop-blur z-20">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Logo compact />
          <div className="flex items-center gap-5">
            <Link to="/" className="font-mono-lux text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-amber-400 transition-colors">
              View Site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 font-mono-lux text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-red-400 transition-colors"
            >
              <LogOut size={13} /> Leave
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <h1 className="font-serif-lux font-light text-4xl text-amber-50">
          Good day, <span className="gold-text italic">Concierge.</span>
        </h1>

        {revenue && (
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#2E3340] border border-[#2E3340]" data-testid="revenue-strip">
            <div className="bg-[#1E2128] p-5">
              <p className="font-mono-lux text-[9px] font-semibold uppercase tracking-[0.3em] text-amber-500">Collected This Week</p>
              <p className="mt-2 font-serif-lux text-4xl gold-text" data-testid="revenue-week">${fmtMoney(revenue.week_total)}</p>
              <p className="mt-1 font-mono-lux text-[9px] uppercase tracking-[0.2em] text-zinc-500" data-testid="revenue-week-count">
                {revenue.week_count} payment{revenue.week_count === 1 ? "" : "s"}
              </p>
            </div>
            <div className="bg-[#1E2128] p-5">
              <p className="font-mono-lux text-[9px] font-semibold uppercase tracking-[0.3em] text-zinc-400">Deposits · This Week</p>
              <p className="mt-2 font-serif-lux text-2xl text-amber-100" data-testid="revenue-deposits">${fmtMoney(revenue.week_deposits)}</p>
            </div>
            <div className="bg-[#1E2128] p-5">
              <p className="font-mono-lux text-[9px] font-semibold uppercase tracking-[0.3em] text-zinc-400">Balances · This Week</p>
              <p className="mt-2 font-serif-lux text-2xl text-amber-100" data-testid="revenue-balances">${fmtMoney(revenue.week_balances)}</p>
            </div>
            <div className="bg-[#1E2128] p-5">
              <p className="font-mono-lux text-[9px] font-semibold uppercase tracking-[0.3em] text-zinc-400">Collected · All Time</p>
              <p className="mt-2 font-serif-lux text-2xl text-amber-100" data-testid="revenue-alltime">${fmtMoney(revenue.all_total)}</p>
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
          {["bookings", "fleet", "enquiries", "reviews", "promos", "vouchers"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t}`}
              className={`font-mono-lux text-[10px] uppercase tracking-[0.25em] px-5 py-2.5 border transition-colors ${
                tab === t ? "border-amber-500 text-amber-300 bg-amber-500/10" : "border-zinc-800 text-zinc-500 hover:border-amber-500/40"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "bookings" && (
          <div className="mt-8" data-testid="admin-bookings-panel">
            <div className="flex flex-wrap gap-2 mb-6">
              {["all", ...STATUSES].map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  data-testid={`booking-filter-${s}`}
                  className={`font-mono-lux text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors ${
                    statusFilter === s ? "border-amber-500 text-amber-300" : "border-zinc-800 text-zinc-600 hover:text-zinc-300"
                  }`}
                >
                  {s}
                </button>
              ))}
              <input
                type="month"
                value={exportMonth}
                onChange={(e) => setExportMonth(e.target.value)}
                data-testid="admin-export-month"
                title="Export one month (leave empty for all time)"
                className="ml-auto bg-[#16181D] border border-zinc-800 text-zinc-400 font-mono-lux text-[10px] uppercase tracking-[0.15em] px-2 py-1.5 [color-scheme:dark]"
              />
              <button
                onClick={exportCsv}
                data-testid="admin-bookings-export"
                className="flex items-center gap-2 font-mono-lux text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-amber-500/40 text-amber-300 hover:bg-amber-500 hover:text-black transition-colors"
              >
                <Download size={12} /> Export CSV
              </button>
              <button
                onClick={purgeCancelled}
                data-testid="purge-cancelled-button"
                title="Permanently delete every cancelled reservation"
                className="flex items-center gap-2 font-mono-lux text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-red-700/40 text-red-400 hover:bg-red-700/20 transition-colors"
              >
                <Trash2 size={12} /> Clear cancelled
              </button>
            </div>
            <div className="flex gap-2 mb-4 max-w-xl" data-testid="booking-search">
              <input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchBookings()}
                placeholder="Search by name, email, phone or reservation reference…"
                className={inputCls}
                data-testid="booking-search-input"
              />
              <button
                onClick={searchBookings}
                disabled={searching}
                data-testid="booking-search-button"
                className="px-4 border border-amber-500/40 text-amber-300 hover:bg-amber-500 hover:text-black transition-colors disabled:opacity-40 flex items-center gap-1.5 font-mono-lux text-[10px] uppercase tracking-[0.2em] whitespace-nowrap"
              >
                <Search size={13} /> {searching ? "…" : "Search"}
              </button>
              {searchQ && (
                <button
                  onClick={() => { setSearchQ(""); refreshBookings(); }}
                  data-testid="booking-search-clear"
                  className="px-3 border border-zinc-800 text-zinc-500 hover:border-amber-500/40 hover:text-amber-300 transition-colors"
                  aria-label="Clear search"
                >
                  <X size={13} />
                </button>
              )}
            </div>
            {filteredBookings.length === 0 ? (
              <p className="text-sm text-zinc-600" data-testid="bookings-empty">No reservations yet.</p>
            ) : (
              <div className="space-y-3">
                {filteredBookings.map((b) => (
                  <div key={b.id} data-testid={`admin-booking-${b.id}`} className="gold-border bg-[#1E2128] p-6 flex flex-wrap items-center gap-6 justify-between">
                    <div>
                      <p className="font-mono-lux text-[10px] tracking-[0.25em] text-amber-500">{b.reference}</p>
                      <p className="font-serif-lux text-xl text-amber-50 mt-1">{b.vehicle_name}</p>
                      <p className="text-xs text-zinc-500 mt-1">
                        {b.mode === "hourly"
                          ? `Hourly Charter · ${b.hours}h`
                          : `Transfer${b.distance_miles ? ` · ${b.distance_miles} mi · ~${b.duration_minutes} min` : ""}`} · {b.date} at {b.time} · {b.pickup} → {b.dropoff || "at direction"}
                      </p>
                      <p className="text-xs text-zinc-600 mt-1 flex flex-wrap items-center gap-2">
                        <span>
                          {b.name} · {b.email}{b.phone ? ` · ${b.phone}` : ""} · {b.passengers} pax / {b.luggage} cases
                          {b.flight_number ? ` · Flight ${b.flight_number}` : ""}
                          {b.is_return ? ` · Return leg of ${b.return_of} (−${b.discount_percent}%)` : ""}
                        </span>
                        {b.client_rides > 1 && (
                          <span data-testid={`repeat-badge-${b.id}`} className="font-mono-lux text-[9px] uppercase tracking-[0.2em] text-amber-300 border border-amber-500/40 px-2 py-0.5">
                            {ordinal(b.client_rides)} ride
                          </span>
                        )}
                        {b.gcal_event_id && (
                          <span data-testid={`gcal-badge-${b.id}`} className="font-mono-lux text-[9px] uppercase tracking-[0.2em] text-emerald-400 border border-emerald-600/50 px-2 py-0.5">
                            On calendar
                          </span>
                        )}
                      </p>
                      {b.notes && <p className="text-xs text-zinc-500 italic mt-1">“{b.notes}”</p>}
                      {(b.stops?.length > 0 || b.add_ons?.length > 0 || b.promo_code || b.voucher_code) && (
                        <p className="text-xs text-zinc-500 mt-1" data-testid={`booking-extras-${b.id}`}>
                          {b.stops?.length > 0 && <>Stops: {b.stops.join(" → ")} · </>}
                          {b.add_ons?.length > 0 && <>Add-ons: {b.add_ons.map((a) => a.label).join(", ")} · </>}
                          {b.promo_code && <span className="text-emerald-400">Promo {b.promo_code} (−${b.discount_amount})</span>}
                          {b.voucher_code && <span className="text-emerald-400">Voucher {b.voucher_code} (−${b.discount_amount})</span>}
                        </p>
                      )}
                      {editingNote === b.id ? (
                        <div className="flex gap-2 mt-2">
                          <input
                            value={noteDraft}
                            onChange={(e) => setNoteDraft(e.target.value)}
                            data-testid={`note-input-${b.id}`}
                            placeholder="VIP preferences — water, temperature, newspaper…"
                            className="flex-1 bg-[#16181D] border border-zinc-800 px-3 py-1.5 text-xs text-zinc-200 focus:border-amber-500/50 outline-none"
                          />
                          <button
                            onClick={() => saveNote(b)}
                            data-testid={`note-save-${b.id}`}
                            className="font-mono-lux text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 bg-amber-500 text-black hover:bg-amber-300 transition-colors"
                          >
                            Save
                          </button>
                        </div>
                      ) : b.client_note ? (
                        <p className="mt-2 text-[11px] text-amber-200/80 italic flex items-center gap-1.5" data-testid={`client-note-${b.id}`}>
                          “{b.client_note}”
                          <button
                            onClick={() => { setEditingNote(b.id); setNoteDraft(b.client_note); }}
                            data-testid={`note-edit-${b.id}`}
                            className="not-italic text-zinc-500 hover:text-amber-300 transition-colors"
                          >
                            <Pencil size={10} />
                          </button>
                        </p>
                      ) : (
                        <button
                          onClick={() => { setEditingNote(b.id); setNoteDraft(""); }}
                          data-testid={`note-add-${b.id}`}
                          className="mt-1.5 font-mono-lux text-[9px] uppercase tracking-[0.2em] text-zinc-600 hover:text-amber-300 transition-colors"
                        >
                          + Client note
                        </button>
                      )}
                      {(tracking[b.id]?.data || tracking[b.id]?.error) && (
                        <p className="mt-2 text-xs font-medium border border-amber-500/30 bg-[#0F1012] px-3 py-2 inline-block" data-testid={`flight-info-${b.id}`}>
                          {tracking[b.id].error ? (
                            <span className="text-red-400">{tracking[b.id].error}</span>
                          ) : (
                            <span className="text-zinc-300">
                              <span className="text-amber-400 font-mono-lux tracking-[0.15em] uppercase">{tracking[b.id].data.flight} · {tracking[b.id].data.airline}</span>
                              {" — "}{tracking[b.id].data.status}
                              {" · "}{tracking[b.id].data.departure_airport}{" "}
                              {tracking[b.id].data.departure_scheduled ? new Date(tracking[b.id].data.departure_scheduled).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                              {tracking[b.id].data.departure_delay ? <span className="text-red-400"> (delayed +{tracking[b.id].data.departure_delay} min)</span> : <span className="text-emerald-400"> (on time)</span>}
                              {" → "}{tracking[b.id].data.arrival_airport}{" "}
                              {tracking[b.id].data.arrival_estimated || tracking[b.id].data.arrival_scheduled ? new Date(tracking[b.id].data.arrival_estimated || tracking[b.id].data.arrival_scheduled).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                              {tracking[b.id].data.arrival_delay ? <span className="text-red-400"> (+{tracking[b.id].data.arrival_delay} min)</span> : ""}
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-3">
                      <span className="font-serif-lux text-2xl gold-text">${b.estimate}</span>
                      {b.deposit_paid && (
                        <span data-testid={`deposit-paid-${b.id}`} className="font-mono-lux text-[9px] uppercase tracking-[0.2em] text-emerald-400 border border-emerald-600/50 px-2 py-1 inline-flex items-center gap-2">
                          Deposit paid ${b.deposit_amount}
                          <button
                            onClick={() => refundPayment(b, "deposit")}
                            data-testid={`refund-deposit-${b.id}`}
                            className="text-red-400/80 hover:text-red-300 underline underline-offset-2 transition-colors"
                          >
                            Refund
                          </button>
                        </span>
                      )}
                      {b.balance_paid && (
                        <span data-testid={`balance-paid-${b.id}`} className="font-mono-lux text-[9px] uppercase tracking-[0.2em] text-emerald-400 border border-emerald-600/50 px-2 py-1 inline-flex items-center gap-2">
                          Balance paid ${b.balance_amount}
                          <button
                            onClick={() => refundPayment(b, "balance")}
                            data-testid={`refund-balance-${b.id}`}
                            className="text-red-400/80 hover:text-red-300 underline underline-offset-2 transition-colors"
                          >
                            Refund
                          </button>
                        </span>
                      )}
                      <div className="flex items-center gap-2 flex-wrap justify-end">
                        {b.status === "confirmed" && (
                          <span className="flex items-center gap-1 mr-1" data-testid={`chauffeur-steps-${b.id}`}>
                            {CHAUFFEUR_STEPS.map((s, i) => {
                              const current = CHAUFFEUR_STEPS.findIndex((x) => x.key === b.chauffeur_status);
                              const state = i <= current ? "bg-amber-500 text-black border-amber-500" : "border-zinc-800 text-zinc-500 hover:border-amber-500/40 hover:text-amber-300";
                              return (
                                <button
                                  key={s.key}
                                  onClick={() => setChauffeur(b.id, s.key)}
                                  data-testid={`chauffeur-${s.key}-${b.id}`}
                                  className={`font-mono-lux text-[9px] uppercase tracking-[0.15em] px-2 py-1.5 border transition-colors ${state}`}
                                >
                                  {s.label}
                                </button>
                              );
                            })}
                          </span>
                        )}
                        {b.status !== "cancelled" && !b.balance_paid && b.estimate - (b.deposit_paid ? b.deposit_amount : 0) > 0 && (
                          <button
                            onClick={() => collectBalance(b)}
                            data-testid={`collect-balance-${b.id}`}
                            className="font-mono-lux text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-amber-500/40 text-amber-300 hover:bg-amber-500 hover:text-black transition-colors"
                          >
                            Collect ${(b.estimate - (b.deposit_paid ? b.deposit_amount : 0)).toFixed(2).replace(/\.00$/, "")}
                          </button>
                        )}
                        {b.status !== "cancelled" && b.balance_checkout_url && !b.balance_paid && (
                          <button
                            onClick={() => copyBalanceLink(b)}
                            data-testid={`copy-balance-link-${b.id}`}
                            className="font-mono-lux text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-zinc-800 text-zinc-400 hover:border-amber-500/40 hover:text-amber-300 transition-colors"
                          >
                            Copy link
                          </button>
                        )}
                        {b.flight_number && (
                          <button
                            onClick={() => trackFlight(b)}
                            data-testid={`track-flight-${b.id}`}
                            className="font-mono-lux text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-amber-500/40 text-amber-300 hover:bg-amber-500 hover:text-black transition-colors"
                          >
                            {tracking[b.id]?.loading ? "…" : "Track"}
                          </button>
                        )}
                        <select
                          value={b.status}
                          onChange={(e) => setStatus(b.id, e.target.value)}
                          data-testid={`booking-status-${b.id}`}
                          className={`bg-[#0F1012] border px-3 py-1.5 font-mono-lux text-[10px] uppercase tracking-[0.2em] outline-none ${STATUS_CLS[b.status]}`}
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => deleteBooking(b)}
                          data-testid={`booking-delete-${b.id}`}
                          title="Delete permanently"
                          className="p-1.5 border border-zinc-800 text-zinc-600 hover:border-red-700/60 hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {moreBookings && (
              <button
                onClick={loadMoreBookings}
                data-testid="admin-bookings-load-more"
                className="mt-6 font-mono-lux text-[10px] uppercase tracking-[0.25em] px-5 py-3 border border-zinc-800 text-zinc-400 hover:border-amber-500/40 hover:text-amber-300 transition-colors"
              >
                Load earlier reservations
              </button>
            )}
          </div>
        )}

        {tab === "fleet" && (
          <div className="mt-8" data-testid="admin-fleet-panel">
            <button
              onClick={() => openEditor(null)}
              data-testid="add-vehicle-button"
              className="flex items-center gap-2 font-mono-lux text-[10px] uppercase tracking-[0.25em] px-5 py-3 bg-amber-500 text-black hover:bg-amber-300 transition-colors mb-6"
            >
              <Plus size={14} /> Add Vehicle
            </button>

            {editing && (
              <form onSubmit={saveVehicle} className="gold-border bg-[#1E2128] p-6 mb-8 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="vehicle-editor-form">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Vehicle name" className={inputCls} data-testid="vehicle-name-input" />
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="vehicle-category-select">
                  {["VIP Sedan", "Executive SUV", "Ultra Luxury", "Armored"].map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
                <div className="sm:col-span-2 flex gap-2">
                  <input required value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="Main image URL — or upload from your device →" className={inputCls} data-testid="vehicle-image-input" />
                  <button
                    type="button"
                    onClick={() => imageFileRef.current?.click()}
                    disabled={!!uploading}
                    data-testid="vehicle-image-upload"
                    className="px-4 border border-amber-500/40 text-amber-300 hover:bg-amber-500 hover:text-black transition-colors disabled:opacity-40 font-mono-lux text-[10px] uppercase tracking-[0.15em] whitespace-nowrap"
                  >
                    {uploading === "image" ? "Uploading…" : "Upload"}
                  </button>
                  <input
                    ref={imageFileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={(e) => { uploadImage(e.target.files?.[0], "image"); e.target.value = ""; }}
                    data-testid="vehicle-image-file"
                  />
                </div>
                {form.image && (
                  <img src={form.image} alt="Vehicle preview" className="sm:col-span-2 h-40 w-full object-cover border border-zinc-800" data-testid="vehicle-image-preview" />
                )}
                <div className="sm:col-span-2 flex gap-2">
                  <input value={form.gallery} onChange={(e) => setForm({ ...form, gallery: e.target.value })} placeholder="Gallery image URLs, comma separated (optional)" className={inputCls} data-testid="vehicle-gallery-input" />
                  <button
                    type="button"
                    onClick={() => galleryFileRef.current?.click()}
                    disabled={!!uploading}
                    data-testid="gallery-upload-button"
                    className="px-4 border border-amber-500/40 text-amber-300 hover:bg-amber-500 hover:text-black transition-colors disabled:opacity-40 font-mono-lux text-[10px] uppercase tracking-[0.15em] whitespace-nowrap"
                  >
                    {uploading === "gallery" ? "Uploading…" : "Upload"}
                  </button>
                  <input
                    ref={galleryFileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={(e) => { uploadImage(e.target.files?.[0], "gallery"); e.target.value = ""; }}
                    data-testid="gallery-file-input"
                  />
                </div>
                <input type="number" min={1} value={form.passengers} onChange={(e) => setForm({ ...form, passengers: e.target.value })} placeholder="Passengers" className={inputCls} data-testid="vehicle-passengers-input" />
                <input type="number" min={0} value={form.luggage} onChange={(e) => setForm({ ...form, luggage: e.target.value })} placeholder="Luggage" className={inputCls} data-testid="vehicle-luggage-input" />
                <input type="number" min={1} value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} placeholder="Rate per hour ($)" className={inputCls} data-testid="vehicle-rate-input" />
                <input value={form.specs} onChange={(e) => setForm({ ...form, specs: e.target.value })} placeholder="Specs, comma separated" className={inputCls} data-testid="vehicle-specs-input" />
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" rows={3} className={`${inputCls} sm:col-span-2 resize-none`} data-testid="vehicle-description-input" />
                <label className="flex items-center gap-3 text-xs text-zinc-400 sm:col-span-2">
                  <input type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })} data-testid="vehicle-available-checkbox" />
                  Available for booking
                </label>
                <div className="sm:col-span-2 flex gap-3">
                  <button type="submit" data-testid="vehicle-save-button" className="flex items-center gap-2 font-mono-lux text-[10px] uppercase tracking-[0.25em] px-6 py-3 bg-amber-500 text-black hover:bg-amber-300 transition-colors">
                    <Check size={13} /> Save
                  </button>
                  <button type="button" onClick={() => setEditing(null)} data-testid="vehicle-cancel-button" className="flex items-center gap-2 font-mono-lux text-[10px] uppercase tracking-[0.25em] px-6 py-3 border border-zinc-800 text-zinc-400 hover:border-amber-500/40 transition-colors">
                    <X size={13} /> Cancel
                  </button>
                </div>
              </form>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {vehicles.map((v) => (
                <div key={v.id} data-testid={`admin-vehicle-${v.id}`} className="gold-border bg-[#1E2128] overflow-hidden flex">
                  <img src={v.image} alt={v.name} className="w-36 object-cover" />
                  <div className="p-5 flex-1">
                    <p className="font-serif-lux text-xl text-amber-50">{v.name}</p>
                    <p className="font-mono-lux text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                      {v.category} · ${v.hourly_rate}/hr · {v.passengers} pax{(v.gallery || []).length ? ` · ${v.gallery.length + 1} photos` : ""}{v.available ? "" : " · unavailable"}
                    </p>
                    <div className="flex gap-2 mt-4">
                      <button onClick={() => openEditor(v)} data-testid={`vehicle-edit-${v.id}`} className="flex items-center gap-1.5 font-mono-lux text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-zinc-800 text-zinc-400 hover:border-amber-500/50 hover:text-amber-300 transition-colors">
                        <Pencil size={11} /> Edit
                      </button>
                      <button onClick={() => removeVehicle(v.id)} data-testid={`vehicle-delete-${v.id}`} className="flex items-center gap-1.5 font-mono-lux text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-zinc-800 text-zinc-400 hover:border-red-700/60 hover:text-red-400 transition-colors">
                        <Trash2 size={11} /> Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 border border-[#2E3340] bg-[#1E2128] p-6" data-testid="blackout-manager">
              <p className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-500">Blackout Dates</p>
              <p className="mt-1 text-xs text-zinc-500">Blocked dates stop new bookings instantly — for servicing, holidays, or full commitments.</p>
              <div className="mt-4 flex flex-wrap gap-3">
                <input
                  type="date"
                  value={blackoutDate}
                  onChange={(e) => setBlackoutDate(e.target.value)}
                  data-testid="blackout-date"
                  className="bg-[#16181D] border border-zinc-800 text-zinc-300 font-mono-lux text-xs px-3 py-2 [color-scheme:dark] focus:border-amber-500/50 outline-none"
                />
                <select
                  value={blackoutVehicle}
                  onChange={(e) => setBlackoutVehicle(e.target.value)}
                  data-testid="blackout-vehicle"
                  className="bg-[#16181D] border border-zinc-800 text-zinc-300 font-mono-lux text-xs px-3 py-2 focus:border-amber-500/50 outline-none"
                >
                  <option value="">Entire fleet</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
                <input
                  value={blackoutReason}
                  onChange={(e) => setBlackoutReason(e.target.value)}
                  placeholder="Reason (optional)"
                  data-testid="blackout-reason"
                  className="flex-1 min-w-40 bg-[#16181D] border border-zinc-800 text-zinc-300 font-mono-lux text-xs px-3 py-2 focus:border-amber-500/50 outline-none"
                />
                <button
                  onClick={addBlackout}
                  data-testid="blackout-add"
                  className="font-mono-lux text-[10px] uppercase tracking-[0.25em] px-4 py-2 bg-amber-500 text-black hover:bg-amber-300 transition-colors"
                >
                  Block date
                </button>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {blackouts.length === 0 && <p className="text-xs text-zinc-600">No blackout dates.</p>}
                {blackouts.map((bl) => (
                  <span
                    key={bl.id}
                    data-testid={`blackout-chip-${bl.id}`}
                    className="inline-flex items-center gap-2 font-mono-lux text-[10px] uppercase tracking-[0.15em] text-zinc-300 border border-zinc-800 px-3 py-1.5"
                  >
                    {bl.date} · {bl.vehicle_id ? vehicles.find((v) => v.id === bl.vehicle_id)?.name || "Vehicle" : "Entire fleet"}{bl.reason ? ` — ${bl.reason}` : ""}
                    <button
                      onClick={() => removeBlackout(bl.id)}
                      data-testid={`blackout-remove-${bl.id}`}
                      className="text-zinc-500 hover:text-red-400 transition-colors"
                    >
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "enquiries" && (
          <div className="mt-8 space-y-3" data-testid="admin-enquiries-panel">
            <div className="flex justify-end">
              <button
                onClick={exportEnquiriesCsv}
                data-testid="admin-enquiries-export"
                className="flex items-center gap-2 font-mono-lux text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-amber-500/40 text-amber-300 hover:bg-amber-500 hover:text-black transition-colors"
              >
                <Download size={12} /> Export CSV
              </button>
            </div>
            {enquiries.length === 0 ? (
              <p className="text-sm text-zinc-600" data-testid="enquiries-empty">No enquiries yet.</p>
            ) : (
              enquiries.map((q) => (
                <div key={q.id} data-testid={`admin-enquiry-${q.id}`} className="gold-border bg-[#1E2128] p-6">
                  <div className="flex flex-wrap justify-between gap-3">
                    <p className="font-serif-lux text-xl text-amber-50">{q.name}</p>
                    <p className="font-mono-lux text-[10px] tracking-[0.2em] text-zinc-600">{new Date(q.created_at).toLocaleString()}</p>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">{q.email}{q.phone ? ` · ${q.phone}` : ""}</p>
                  <p className="text-sm text-zinc-300 mt-3 leading-relaxed">{q.message}</p>
                </div>
              ))
            )}
            {moreEnquiries && (
              <button
                onClick={loadMoreEnquiries}
                data-testid="admin-enquiries-load-more"
                className="font-mono-lux text-[10px] uppercase tracking-[0.25em] px-5 py-3 border border-zinc-800 text-zinc-400 hover:border-amber-500/40 hover:text-amber-300 transition-colors"
              >
                Load earlier enquiries
              </button>
            )}
          </div>
        )}

        {tab === "reviews" && (
          <div className="mt-8 space-y-3" data-testid="admin-reviews-panel">
            {reviews.length === 0 ? (
              <p className="text-sm text-zinc-600" data-testid="reviews-empty">
                No client reviews yet — they arrive from the post-ride email link or the /review page.
              </p>
            ) : (
              reviews.map((r) => (
                <div key={r.id} data-testid={`admin-review-${r.id}`} className="gold-border bg-[#1E2128] p-6 flex flex-wrap items-start justify-between gap-4">
                  <div className="flex-1 min-w-60">
                    <div className="flex flex-wrap items-center gap-3">
                      <p className="font-serif-lux text-xl text-amber-50">{r.name}</p>
                      <span className="font-mono-lux text-[10px] tracking-[0.2em] text-amber-400">{r.rating}/5</span>
                      <span
                        className={`font-mono-lux text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 border ${
                          r.approved ? "border-emerald-600/50 text-emerald-400" : "border-yellow-600/50 text-yellow-500"
                        }`}
                        data-testid={`review-state-${r.id}`}
                      >
                        {r.approved ? "Published" : "Awaiting moderation"}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-300 mt-2 leading-relaxed">“{r.text}”</p>
                    <p className="text-[10px] text-zinc-600 mt-2 font-mono-lux tracking-[0.15em]">
                      {r.booking_reference} · {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => toggleReview(r, !r.approved)}
                      data-testid={`review-toggle-${r.id}`}
                      className="font-mono-lux text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-amber-500/40 text-amber-300 hover:bg-amber-500 hover:text-black transition-colors"
                    >
                      {r.approved ? "Unpublish" : "Publish"}
                    </button>
                    <button
                      onClick={() => deleteReview(r.id)}
                      data-testid={`review-delete-${r.id}`}
                      className="font-mono-lux text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-zinc-800 text-zinc-500 hover:border-red-700/60 hover:text-red-400 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "promos" && (
          <div className="mt-8" data-testid="admin-promos-panel">
            <form onSubmit={createPromo} className="gold-border bg-[#1E2128] p-6 mb-6 grid grid-cols-2 sm:grid-cols-6 gap-3" data-testid="promo-create-form">
              <input required value={promoForm.code} onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })} placeholder="CODE" className={inputCls} data-testid="promo-code-input" />
              <input type="number" min="0" max="100" value={promoForm.percent} onChange={(e) => setPromoForm({ ...promoForm, percent: e.target.value })} placeholder="% off" className={inputCls} data-testid="promo-percent-input" />
              <input type="number" min="0" value={promoForm.amount_off} onChange={(e) => setPromoForm({ ...promoForm, amount_off: e.target.value })} placeholder="$ off" className={inputCls} data-testid="promo-amount-input" />
              <input type="date" value={promoForm.expires} onChange={(e) => setPromoForm({ ...promoForm, expires: e.target.value })} title="Expiry (optional)" className={`${inputCls} [color-scheme:dark]`} data-testid="promo-expires-input" />
              <input type="number" min="0" value={promoForm.max_uses} onChange={(e) => setPromoForm({ ...promoForm, max_uses: e.target.value })} placeholder="Max uses (0=∞)" className={inputCls} data-testid="promo-max-uses-input" />
              <button type="submit" data-testid="promo-create-button" className="font-mono-lux text-[10px] uppercase tracking-[0.25em] px-4 py-3 bg-amber-500 text-black hover:bg-amber-300 transition-colors">
                Create
              </button>
            </form>
            <div className="space-y-3">
              {promos.length === 0 && <p className="text-sm text-zinc-600" data-testid="promos-empty">No promo codes yet.</p>}
              {promos.map((p) => (
                <div key={p.id} data-testid={`admin-promo-${p.id}`} className="gold-border bg-[#1E2128] p-5 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="font-mono-lux text-sm tracking-[0.2em] text-amber-300">{p.code}</p>
                    <p className="text-xs text-zinc-500 mt-1">
                      {p.percent ? `${p.percent}% off` : `$${p.amount_off} off`}
                      {p.expires ? ` · expires ${p.expires}` : " · no expiry"}
                      {p.max_uses ? ` · ${p.used_count}/${p.max_uses} used` : ` · ${p.used_count} used`}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => togglePromo(p, !p.active)}
                      data-testid={`promo-toggle-${p.id}`}
                      className={`font-mono-lux text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors ${
                        p.active
                          ? "border-emerald-600/50 text-emerald-400 hover:border-red-700/60 hover:text-red-400"
                          : "border-zinc-800 text-zinc-500 hover:border-amber-500/40 hover:text-amber-300"
                      }`}
                    >
                      {p.active ? "Live — pause" : "Paused — activate"}
                    </button>
                    <button
                      onClick={() => deletePromo(p.id)}
                      data-testid={`promo-delete-${p.id}`}
                      className="font-mono-lux text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-zinc-800 text-zinc-500 hover:border-red-700/60 hover:text-red-400 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "vouchers" && (
          <div className="mt-8" data-testid="admin-vouchers-panel">
            {vouchers.length === 0 ? (
              <p className="text-sm text-zinc-600" data-testid="vouchers-empty">No gift vouchers sold yet — they're purchased at /gift.</p>
            ) : (
              <div className="space-y-3">
                {vouchers.map((v) => (
                  <div key={v.id} data-testid={`admin-voucher-${v.id}`} className="gold-border bg-[#1E2128] p-5 flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <p className="font-mono-lux text-sm tracking-[0.2em] text-amber-300">{v.code}</p>
                      <p className="text-xs text-zinc-500 mt-1">
                        {v.buyer_name} · {v.buyer_email} · {v.created_at ? new Date(v.created_at).toLocaleDateString() : ""}
                        {v.last_booking ? ` · last used on ${v.last_booking}` : ""}
                      </p>
                    </div>
                    <p className="text-right">
                      <span className="font-serif-lux text-2xl gold-text">${v.remaining}</span>
                      <span className="block font-mono-lux text-[9px] uppercase tracking-[0.2em] text-zinc-500">of ${v.amount} remaining</span>
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
